<script setup>
// Auto-extracted chrome for Hausautarkie.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Hausautarkie" },
    brandTagline: { type: String, default: "Das System rechnet sich nur als Ganzes." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Hausautarkie");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Das System rechnet sich nur als Ganzes.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700;800;900&display=swap";
</script>

<template>
    <div class="haut" :style="`--haut-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="haut-nav">
                    <div class="haut-nav__inner">
                        <a class="haut-logo" href="/" aria-label="Startseite Hausautarkie">
                            <span class="haut-logo__mark" aria-hidden="true">
                                <span class="haut-logo__sun"></span>
                                <span class="haut-logo__bar"></span>
                                <span class="haut-logo__bar haut-logo__bar--2"></span>
                            </span>
                            <span class="haut-logo__txt">
                                <span class="haut-logo__name">Hausautarkie</span>
                                <span class="haut-logo__sub">{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="haut-nav__links" aria-label="Sekundärnavigation">
                            <a href="/">Magazin</a>
                            <a href="#methodik">Methodik</a>
                            <a href="#faq">Fragen</a>
                            <a href="#haut-stepper" class="haut-nav__cta">Rechner starten</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="haut-foot">
                    <div class="haut-foot__inner">
                        <div class="haut-foot__brand">
                            <span class="haut-logo__mark" aria-hidden="true">
                                <span class="haut-logo__sun"></span>
                                <span class="haut-logo__bar"></span>
                                <span class="haut-logo__bar haut-logo__bar--2"></span>
                            </span>
                            <span>Hausautarkie &middot; {{ brandTagline }}</span>
                        </div>
                        <nav class="haut-foot__links" aria-label="Footer">
                            <a href="/">Magazin</a>
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                        <p class="haut-foot__copy">&copy; {{ new Date().getFullYear() }} hausautarkie.com &middot; alle Berechnungen ohne Gewähr, ersetzen keine Fachplanung nach VDI 4655.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

/* haut-* namespace. Space Grotesk display + Inter body. Lime accent #84CC16
   für CTA. Hairline-only architecture, BIG number result, sticky mobile CTA. */

.haut {
    --haut-ink: #0f1310;
    --haut-ink-2: #353a36;
    --haut-mute: #71776f;
    --haut-cream: #f4f7ee;
    --haut-cream-2: #e8efdb;
    --haut-surface: #fafcf4;
    --haut-white: #ffffff;
    --haut-line: rgba(15, 19, 16, 0.08);
    --haut-line-strong: rgba(15, 19, 16, 0.16);
    --haut-cta: var(--haut-accent, #84CC16);
    --haut-cta-hover: #65a30d;
    --haut-cta-ink: #0f1310;
    --haut-cta-glow: 0 8px 24px -8px rgba(132,204,22,0.55), 0 0 0 1px rgba(132,204,22,0.22);
    --haut-warm: #c2410c;
    --haut-cool: #475569;
    --haut-ease: cubic-bezier(0.32, 0.72, 0, 1);

    background: var(--haut-cream);
    color: var(--haut-ink);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 17px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100dvh;
    padding-bottom: 88px;
    text-rendering: optimizeLegibility;
}
.haut * { box-sizing: border-box; }
.haut a { color: inherit; }
.haut input, .haut button, .haut output { font-family: inherit; }
.haut-num-tab { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum'; }

/* Nav */
.haut-nav {
    background: rgba(244, 247, 238, 0.85);
    backdrop-filter: saturate(160%) blur(12px);
    -webkit-backdrop-filter: saturate(160%) blur(12px);
    border-bottom: 1px solid var(--haut-line);
    position: sticky;
    top: 0;
    z-index: 30;
}
.haut-nav__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}
.haut-logo { display: inline-flex; align-items: center; gap: 12px; text-decoration: none; color: var(--haut-ink); }
.haut-logo__mark {
    width: 30px; height: 30px;
    background: var(--haut-ink);
    border-radius: 8px;
    display: inline-flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    padding: 3px;
    position: relative;
    gap: 2px;
}
.haut-logo__sun {
    width: 9px; height: 9px;
    border-radius: 50%;
    background: var(--haut-accent);
    position: absolute;
    top: 4px;
    right: 4px;
    box-shadow: 0 0 0 3px rgba(132,204,22,0.25);
}
.haut-logo__bar { width: 18px; height: 4px; background: var(--haut-cream); border-radius: 1px; }
.haut-logo__bar--2 { width: 14px; opacity: 0.6; }
.haut-logo__txt { display: flex; flex-direction: column; line-height: 1.15; }
.haut-logo__name { font-weight: 600; font-size: 15px; letter-spacing: -0.01em; }
.haut-logo__sub { font-size: 11px; color: var(--haut-mute); letter-spacing: 0.02em; }
.haut-nav__links { display: none; align-items: center; gap: 28px; font-size: 14px; }
.haut-nav__links a { text-decoration: none; color: var(--haut-ink-2); transition: color 180ms var(--haut-ease); }
.haut-nav__links a:hover { color: var(--haut-ink); }
.haut-nav__cta {
    padding: 9px 16px;
    background: var(--haut-ink);
    color: var(--haut-cream) !important;
    border-radius: 999px;
    font-weight: 500;
}
.haut-nav__cta:hover { background: var(--haut-accent); color: var(--haut-cta-ink) !important; }
@media (min-width: 720px) { .haut-nav__links { display: inline-flex; } }

/* Hero */
.haut-hero { padding: 28px 20px 8px; position: relative; }
.haut-hero__inner {
    max-width: 1180px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 28px;
    align-items: stretch;
}
.haut-hero__kicker {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    color: var(--haut-ink-2);
    margin: 0 0 22px;
    padding: 8px 14px 8px 12px;
    background: var(--haut-surface);
    border: 1px solid var(--haut-line);
    border-radius: 999px;
    letter-spacing: 0.01em;
    width: max-content;
}
.haut-hero__dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--haut-accent);
    box-shadow: 0 0 0 4px rgba(132,204,22,0.18);
}
.haut-hero__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(38px, 8vw, 76px);
    line-height: 1.0;
    letter-spacing: -0.035em;
    margin: 0 0 18px;
    color: var(--haut-ink);
    text-wrap: balance;
}
.haut-hero__em {
    font-style: normal;
    font-weight: 700;
    color: var(--haut-cta-hover);
    letter-spacing: -0.035em;
    background: linear-gradient(transparent 62%, rgba(132,204,22,0.32) 62%);
    padding: 0 3px;
}
.haut-hero__sub {
    font-size: clamp(16px, 2.2vw, 19px);
    line-height: 1.5;
    color: var(--haut-ink-2);
    margin: 0 0 28px;
    max-width: 56ch;
}
.haut-hero__ctaRow { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }

/* Sample bilanz card */
.haut-hero__sample { display: flex; align-items: flex-start; justify-content: center; }
.haut-sample {
    background: var(--haut-white);
    border: 1px solid var(--haut-line);
    border-radius: 20px;
    padding: 22px 22px 18px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 1px 0 rgba(255,255,255,0.5) inset, 0 24px 60px -32px rgba(15,19,16,0.18);
    position: relative;
    overflow: hidden;
}
.haut-sample::after {
    content: '';
    position: absolute;
    inset: auto 0 0 0;
    height: 4px;
    background: var(--haut-accent);
}
.haut-sample__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.haut-sample__tag { font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--haut-mute); font-weight: 500; }
.haut-sample__dot { width: 9px; height: 9px; border-radius: 50%; background: var(--haut-accent); box-shadow: 0 0 0 4px rgba(132,204,22,0.18); }
.haut-sample__label { font-size: 12px; color: var(--haut-mute); margin: 0 0 4px; }
.haut-sample__big {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(54px, 11vw, 84px);
    letter-spacing: -0.045em;
    line-height: 1;
    margin: 0 0 18px;
    color: var(--haut-cta-hover);
    font-variant-numeric: tabular-nums;
}
.haut-sample__bigUnit { font-family: 'Inter', sans-serif; font-size: 22px; font-weight: 500; color: var(--haut-cta-hover); margin-left: 6px; vertical-align: 0.32em; }
.haut-sample__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 13px;
    color: var(--haut-ink-2);
    padding: 8px 0;
    border-top: 1px solid var(--haut-line);
    margin: 0;
}
.haut-sample__row span:last-child { font-weight: 600; color: var(--haut-ink); }
.haut-sample__row--strong span:last-child { color: var(--haut-cta-hover); }
.haut-sample__foot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--haut-line);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--haut-mute);
}
.haut-sample__foot svg { color: var(--haut-cta-hover); flex: none; }

/* Trust strip */
.haut-trust { margin-top: 36px; border-top: 1px solid var(--haut-line); border-bottom: 1px solid var(--haut-line); }
.haut-trust__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 18px 20px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}
.haut-trust__item { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; font-size: 13px; }
.haut-trust__k { color: var(--haut-mute); letter-spacing: 0.12em; text-transform: uppercase; font-size: 11px; font-weight: 500; }
.haut-trust__v { color: var(--haut-ink); font-weight: 500; text-align: right; }

@media (min-width: 880px) {
    .haut-hero { padding: 56px 32px 16px; }
    .haut-hero__inner { grid-template-columns: 1.25fr 1fr; gap: 56px; }
    .haut-hero__sample { justify-content: flex-end; align-items: center; }
    .haut-sample { max-width: 440px; }
    .haut-trust__inner { grid-template-columns: repeat(3, 1fr); gap: 28px; padding: 22px 32px; }
    .haut-trust__item { flex-direction: column; align-items: flex-start; gap: 4px; }
    .haut-trust__v { text-align: left; }
}

/* Stepper */
.haut-step { padding: 48px 20px 64px; }
.haut-step__inner { max-width: 820px; margin: 0 auto; }
.haut-step__head { display: flex; flex-direction: column; gap: 12px; margin-bottom: 28px; }
.haut-step__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.14em;
    color: var(--haut-cta-hover);
    margin: 0;
    padding: 6px 12px;
    background: rgba(132,204,22,0.12);
    border: 1px solid rgba(132,204,22,0.28);
    border-radius: 999px;
    width: max-content;
    font-variant-numeric: tabular-nums;
}
.haut-step__titleWrap { min-width: 0; }
.haut-step__kicker { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--haut-mute); margin: 0 0 4px; font-weight: 500; }
.haut-step__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(24px, 4vw, 32px);
    letter-spacing: -0.02em;
    margin: 0;
    color: var(--haut-ink);
    line-height: 1.1;
}
.haut-step__prog { height: 3px; background: var(--haut-line); border-radius: 1px; overflow: hidden; margin-top: 8px; }
.haut-step__progFill { display: block; height: 100%; background: var(--haut-accent); transition: width 320ms var(--haut-ease); }

.haut-chapter { display: flex; flex-direction: column; gap: 36px; }
.haut-field { display: flex; flex-direction: column; gap: 14px; }
.haut-field__lbl {
    display: flex;
    align-items: baseline;
    gap: 12px;
    font-size: 16px;
    font-weight: 500;
    color: var(--haut-ink);
}
.haut-field__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 13px;
    color: var(--haut-mute);
    font-weight: 400;
    font-variant-numeric: tabular-nums;
    min-width: 22px;
}

.haut-radios { display: grid; gap: 10px; }
@media (min-width: 600px) {
    .haut-radios--2 { grid-template-columns: 1fr 1fr; }
}
.haut-radio {
    appearance: none;
    background: var(--haut-surface);
    border: 1px solid var(--haut-line);
    border-radius: 12px;
    padding: 14px 16px;
    text-align: left;
    cursor: pointer;
    color: var(--haut-ink);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 60px;
    transition: border-color 180ms var(--haut-ease), background 180ms var(--haut-ease), transform 100ms var(--haut-ease);
    font: inherit;
}
.haut-radio:hover { border-color: var(--haut-line-strong); background: var(--haut-white); }
.haut-radio:active { transform: translateY(1px); }
.haut-radio--on {
    border-color: var(--haut-accent);
    background: var(--haut-white);
    box-shadow: 0 0 0 3px rgba(132,204,22,0.14);
}
.haut-radio__bullet {
    width: 18px; height: 18px;
    border-radius: 50%;
    border: 1.5px solid var(--haut-line-strong);
    display: inline-flex; align-items: center; justify-content: center;
    margin-top: 1px; flex: none;
    background: var(--haut-white);
    transition: border-color 180ms var(--haut-ease);
}
.haut-radio--on .haut-radio__bullet { border-color: var(--haut-accent); }
.haut-radio__bulletDot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--haut-accent);
    transform: scale(0);
    transition: transform 180ms var(--haut-ease);
}
.haut-radio--on .haut-radio__bulletDot { transform: scale(1); }
.haut-radio__body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.haut-radio__main { font-weight: 500; font-size: 15px; line-height: 1.3; }
.haut-radio__hint { font-size: 13px; color: var(--haut-mute); line-height: 1.4; }

/* Slider */
.haut-slider {
    background: var(--haut-surface);
    border: 1px solid var(--haut-line);
    border-radius: 12px;
    padding: 18px 18px 14px;
}
.haut-slider__row { display: flex; align-items: center; gap: 16px; }
.haut-slider input[type=range] {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    height: 28px;
    min-height: 44px;
}
.haut-slider input[type=range]::-webkit-slider-runnable-track { height: 4px; background: var(--haut-line-strong); border-radius: 2px; }
.haut-slider input[type=range]::-moz-range-track { height: 4px; background: var(--haut-line-strong); border-radius: 2px; }
.haut-slider input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--haut-accent);
    border: 3px solid var(--haut-white);
    box-shadow: 0 1px 6px rgba(132,204,22,0.5);
    margin-top: -9px; cursor: grab;
}
.haut-slider input[type=range]::-moz-range-thumb {
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--haut-accent);
    border: 3px solid var(--haut-white);
    box-shadow: 0 1px 6px rgba(132,204,22,0.5);
    cursor: grab;
}
.haut-slider input[type=range]:focus { outline: none; }
.haut-slider input[type=range]:focus::-webkit-slider-thumb { box-shadow: 0 0 0 4px rgba(132,204,22,0.22); }
.haut-slider output {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 22px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--haut-ink);
    min-width: 116px;
    text-align: right;
}
.haut-slider__ticks { display: flex; justify-content: space-between; margin-top: 10px; font-size: 11px; color: var(--haut-mute); letter-spacing: 0.04em; }

/* Text field PLZ */
.haut-textfield {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--haut-surface);
    border: 1px solid var(--haut-line);
    border-radius: 12px;
    padding: 4px 16px;
    transition: border-color 180ms var(--haut-ease), background 180ms var(--haut-ease);
}
.haut-textfield:focus-within { border-color: var(--haut-accent); background: var(--haut-white); box-shadow: 0 0 0 3px rgba(132,204,22,0.14); }
.haut-textfield input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 18px;
    font-weight: 500;
    padding: 14px 0;
    color: var(--haut-ink);
    min-height: 48px;
    font-variant-numeric: tabular-nums;
}
.haut-textfield__unit { font-size: 13px; color: var(--haut-mute); white-space: nowrap; }
.haut-textfield--plz input { letter-spacing: 0.08em; }

/* Step nav */
.haut-step__nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-top: 24px;
    margin-top: 8px;
    border-top: 1px solid var(--haut-line);
}

/* Buttons */
.haut-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: inherit;
    font-weight: 500;
    font-size: 15px;
    letter-spacing: -0.005em;
    line-height: 1;
    padding: 14px 20px;
    min-height: 48px;
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
    transition: background 180ms var(--haut-ease), color 180ms var(--haut-ease), border-color 180ms var(--haut-ease), transform 100ms var(--haut-ease);
    text-decoration: none;
    white-space: nowrap;
}
.haut-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.haut-btn:active:not(:disabled) { transform: translateY(1px); }
.haut-btn__arr { transition: transform 220ms var(--haut-ease); }
.haut-btn:not(:disabled):hover .haut-btn__arr { transform: translateX(3px); }

.haut-btn--primary {
    background: var(--haut-cta);
    color: var(--haut-cta-ink);
    box-shadow: var(--haut-cta-glow);
    font-weight: 700;
    letter-spacing: -0.012em;
    transition: background 180ms var(--haut-ease), box-shadow 200ms var(--haut-ease), transform 180ms var(--haut-ease);
}
.haut-btn--primary:hover:not(:disabled) {
    background: var(--haut-cta-hover);
    color: var(--haut-white);
    box-shadow: 0 12px 32px -8px rgba(132,204,22,0.6), 0 0 0 1px rgba(132,204,22,0.28);
    transform: translateY(-1px);
}
.haut-btn--ghost { background: transparent; color: var(--haut-ink); border-color: var(--haut-line-strong); }
.haut-btn--ghost:hover:not(:disabled) { background: var(--haut-surface); border-color: var(--haut-ink); }
.haut-btn--lg { padding: 22px 32px; font-size: 19px; min-height: 60px; font-weight: 800; letter-spacing: -0.01em; }
.haut-btn--full { width: 100%; }
.haut-btn--stick { padding: 12px 20px; min-height: 48px; font-size: 15px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
.haut-btn--shout { text-transform: uppercase; letter-spacing: 0.08em; font-weight: 900; font-size: 19px; }
@media (min-width: 720px) { .haut-btn--shout { font-size: 22px; letter-spacing: 0.09em; } }

.haut-hero__meta, .haut-form__meta {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--haut-mute);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-variant-numeric: tabular-nums;
    margin: 0;
}
.haut-form__meta { text-align: center; margin-top: 8px; }

.haut-back-link {
    background: transparent;
    border: 0;
    color: var(--haut-mute);
    font-size: 14px;
    font-weight: 500;
    padding: 12px 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: color 180ms var(--haut-ease);
    min-height: 44px;
}
.haut-back-link:hover { color: var(--haut-ink); }

/* Result */
.haut-result {
    background: var(--haut-white);
    border: 1px solid var(--haut-line);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
}
.haut-result::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: var(--haut-accent);
}
.haut-result__inner { padding: 36px 26px 28px; }

.haut-money { padding-bottom: 24px; border-bottom: 1px solid var(--haut-line); }
.haut-money__label { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--haut-mute); margin: 0 0 14px; font-weight: 500; }
.haut-money__value {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(72px, 14vw, 128px);
    line-height: 0.95;
    letter-spacing: -0.055em;
    margin: 0 0 12px;
    color: var(--haut-ink);
    font-variant-numeric: tabular-nums;
    text-wrap: balance;
}
.haut-money__num { color: var(--haut-cta-hover); }
.haut-money__cur {
    font-family: 'Inter', sans-serif;
    font-size: clamp(28px, 5vw, 44px);
    color: var(--haut-cta-hover);
    font-weight: 500;
    margin-left: 12px;
    vertical-align: 0.4em;
    letter-spacing: 0;
}
.haut-money__hint { font-size: 14px; color: var(--haut-ink-2); margin: 0 0 16px; }

.haut-score {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px 8px 12px;
    background: var(--haut-cream);
    border: 1px solid var(--haut-line);
    border-radius: 999px;
    font-size: 13px;
    font-weight: 500;
    color: var(--haut-ink);
}
.haut-score__pip { width: 10px; height: 10px; border-radius: 50%; }
.haut-score--hot .haut-score__pip { background: var(--haut-accent); box-shadow: 0 0 0 3px rgba(132,204,22,0.22); }
.haut-score--warm .haut-score__pip { background: var(--haut-warm); box-shadow: 0 0 0 3px rgba(194,65,12,0.18); }
.haut-score--cool .haut-score__pip { background: var(--haut-cool); box-shadow: 0 0 0 3px rgba(71,85,105,0.18); }

.haut-kpis {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    margin: 24px 0 24px;
    border-top: 1px solid var(--haut-line);
    border-bottom: 1px solid var(--haut-line);
}
.haut-kpis div {
    padding: 18px 18px;
    border-right: 1px solid var(--haut-line);
    border-bottom: 1px solid var(--haut-line);
}
.haut-kpis div:nth-child(2n) { border-right: 0; }
.haut-kpis div:nth-last-child(-n+2) { border-bottom: 0; }
.haut-kpis dt { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--haut-mute); margin-bottom: 8px; font-weight: 500; }
.haut-kpis dd {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--haut-ink);
    line-height: 1;
}
.haut-kpis dd span { font-family: 'Inter', sans-serif; font-size: 13px; color: var(--haut-mute); font-weight: 400; margin-left: 4px; letter-spacing: 0; }
.haut-kpis__win { color: var(--haut-cta-hover) !important; }
@media (min-width: 640px) {
    .haut-kpis { grid-template-columns: repeat(4, 1fr); }
    .haut-kpis div:nth-child(2n) { border-right: 1px solid var(--haut-line); }
    .haut-kpis div:nth-child(4n) { border-right: 0; }
    .haut-kpis div { border-bottom: 0 !important; }
}

.haut-method { margin-top: 8px; font-size: 13px; color: var(--haut-ink-2); }
.haut-method summary {
    cursor: pointer;
    color: var(--haut-ink);
    font-weight: 500;
    padding: 8px 0;
    list-style: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.haut-method summary::after { content: '+'; font-size: 18px; color: var(--haut-mute); transition: transform 180ms var(--haut-ease); display: inline-block; }
.haut-method[open] summary::after { transform: rotate(45deg); }
.haut-method ul { margin: 8px 0 0; padding-left: 20px; }
.haut-method li { margin: 6px 0; line-height: 1.55; }

.haut-lead {
    padding: 32px 26px 28px;
    background: var(--haut-cream);
    border-top: 1px solid var(--haut-line);
}
.haut-lead__head { margin-bottom: 24px; }
.haut-lead__eyebrow { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--haut-cta-hover); margin: 0 0 6px; font-weight: 600; }
.haut-lead__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(22px, 3.4vw, 28px);
    letter-spacing: -0.02em;
    margin: 0 0 8px;
    line-height: 1.2;
    color: var(--haut-ink);
    text-wrap: balance;
}
.haut-lead__sub { font-size: 15px; color: var(--haut-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }

.haut-form { display: flex; flex-direction: column; gap: 16px; }
.haut-form__grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media (min-width: 560px) { .haut-form__grid { grid-template-columns: 1fr 1fr; } }

.haut-input { display: flex; flex-direction: column; gap: 6px; }
.haut-input__lbl { font-size: 13px; font-weight: 500; color: var(--haut-ink); }
.haut-input__lbl em { font-weight: 400; font-style: italic; color: var(--haut-mute); }
.haut-input input {
    font-size: 16px;
    padding: 14px 16px;
    min-height: 50px;
    border: 1px solid var(--haut-line-strong);
    border-radius: 10px;
    background: var(--haut-white);
    color: var(--haut-ink);
    transition: border-color 180ms var(--haut-ease), box-shadow 180ms var(--haut-ease);
}
.haut-input input:focus { outline: 0; border-color: var(--haut-accent); box-shadow: 0 0 0 3px rgba(132,204,22,0.18); }
.haut-input--err input { border-color: var(--haut-warm); }
.haut-input__err { font-size: 12px; color: var(--haut-warm); margin: 4px 0 0; font-weight: 500; }
.haut-input__err--center { text-align: center; margin-top: 8px; }

.haut-consent {
    display: grid;
    grid-template-columns: 22px 1fr;
    gap: 12px;
    align-items: flex-start;
    font-size: 12px;
    color: var(--haut-mute);
    line-height: 1.55;
    padding: 16px 0 4px;
    border-top: 1px solid var(--haut-line);
    margin-top: 4px;
}
.haut-consent input {
    width: 20px; height: 20px;
    margin-top: 2px;
    accent-color: var(--haut-accent);
    cursor: pointer;
}
.haut-consent__txt { display: block; }
.haut-consent--err { color: var(--haut-warm); }

.haut-done {
    padding: 48px 28px;
    background: var(--haut-cream);
    border-top: 1px solid var(--haut-line);
    text-align: center;
    color: var(--haut-ink);
}
.haut-done svg { color: var(--haut-cta-hover); margin-bottom: 16px; }
.haut-done h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: 26px;
    letter-spacing: -0.02em;
    margin: 0 0 10px;
}
.haut-done p { font-size: 15px; color: var(--haut-ink-2); margin: 0 auto; max-width: 46ch; }

.haut-result__back { padding: 0 26px 22px; }

/* Methodology */
.haut-meth {
    padding: 72px 20px 56px;
    background: var(--haut-surface);
    border-top: 1px solid var(--haut-line);
    border-bottom: 1px solid var(--haut-line);
}
.haut-meth__inner { max-width: 1080px; margin: 0 auto; }
.haut-meth__head { margin-bottom: 36px; max-width: 780px; }
.haut-meth__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--haut-cta-hover); margin: 0 0 12px; font-weight: 600; }
.haut-meth__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(28px, 5vw, 48px);
    letter-spacing: -0.025em;
    line-height: 1.08;
    margin: 0 0 14px;
    color: var(--haut-ink);
    text-wrap: balance;
}
.haut-meth__h em { font-style: normal; font-weight: 700; color: var(--haut-cta-hover); }
.haut-meth__lead { font-size: 17px; color: var(--haut-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }
.haut-meth__author { font-size: 13px; color: var(--haut-mute); margin: 14px 0 0; }
.haut-meth__author strong { color: var(--haut-ink); font-weight: 600; }

.haut-meth__list { list-style: none; padding: 0; margin: 0; }
.haut-meth__row {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 20px;
    padding: 28px 0;
    border-top: 1px solid var(--haut-line);
    align-items: start;
}
.haut-meth__row:last-child { border-bottom: 1px solid var(--haut-line); }
.haut-meth__rowNum {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: var(--haut-mute);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
    margin-top: 4px;
}
.haut-meth__row h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(20px, 2.6vw, 24px);
    letter-spacing: -0.015em;
    margin: 0 0 8px;
    line-height: 1.25;
}
.haut-meth__row p { font-size: 15px; color: var(--haut-ink-2); margin: 0; line-height: 1.55; max-width: 60ch; }
.haut-meth__rowTag {
    display: none;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--haut-mute);
    font-weight: 500;
    padding: 6px 12px;
    border: 1px solid var(--haut-line);
    border-radius: 999px;
    background: var(--haut-cream);
    align-self: start;
    margin-top: 6px;
    white-space: nowrap;
}
@media (min-width: 860px) {
    .haut-meth__row { grid-template-columns: 56px 1fr auto; gap: 32px; }
    .haut-meth__rowTag { display: inline-flex; align-items: center; }
}
.haut-meth__fineprint { font-size: 13px; color: var(--haut-mute); margin: 24px 0 0; line-height: 1.55; max-width: 64ch; }

/* FAQ */
.haut-faq { padding: 72px 20px 88px; }
.haut-faq__inner { max-width: 880px; margin: 0 auto; }
.haut-faq__head { margin-bottom: 32px; }
.haut-faq__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--haut-cta-hover); margin: 0 0 12px; font-weight: 600; }
.haut-faq__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(28px, 5vw, 44px);
    letter-spacing: -0.025em;
    line-height: 1.1;
    margin: 0;
    text-wrap: balance;
}
.haut-faq__h em { font-style: normal; font-weight: 700; color: var(--haut-cta-hover); }
.haut-faq__list { margin: 0; padding: 0; }
.haut-faq__item { padding: 22px 0; border-top: 1px solid var(--haut-line); }
.haut-faq__item:last-child { border-bottom: 1px solid var(--haut-line); }
.haut-faq__item dt {
    display: flex;
    gap: 14px;
    font-size: 17px;
    font-weight: 500;
    color: var(--haut-ink);
    margin-bottom: 8px;
    line-height: 1.35;
    letter-spacing: -0.01em;
}
.haut-faq__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 400;
    font-size: 13px;
    color: var(--haut-mute);
    min-width: 24px;
    margin-top: 3px;
    font-variant-numeric: tabular-nums;
}
.haut-faq__item dd { margin: 0 0 0 38px; font-size: 15px; color: var(--haut-ink-2); line-height: 1.55; max-width: 60ch; }

/* Footer */
.haut-foot { background: var(--haut-ink); color: rgba(244,247,238,0.7); padding: 36px 20px 40px; }
.haut-foot__inner { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 18px; }
.haut-foot__brand { display: inline-flex; align-items: center; gap: 12px; color: var(--haut-cream); font-size: 14px; font-weight: 500; }
.haut-foot__links { display: flex; gap: 24px; font-size: 14px; }
.haut-foot__links a { color: rgba(244,247,238,0.7); text-decoration: none; transition: color 180ms var(--haut-ease); }
.haut-foot__links a:hover { color: var(--haut-cream); }
.haut-foot__copy { font-size: 12px; color: rgba(244,247,238,0.45); margin: 0; line-height: 1.55; max-width: 64ch; }
@media (min-width: 720px) {
    .haut-foot__inner { grid-template-columns: auto 1fr auto; align-items: center; gap: 24px; }
    .haut-foot__copy { grid-column: 1 / -1; padding-top: 18px; border-top: 1px solid rgba(244,247,238,0.1); }
}

/* Sticky CTA */
.haut-stick {
    position: fixed;
    left: 12px; right: 12px;
    bottom: 12px;
    z-index: 40;
    background: var(--haut-ink);
    color: var(--haut-cream);
    border-radius: 14px;
    padding: 10px 12px 10px 18px;
    padding-bottom: max(10px, env(safe-area-inset-bottom, 10px));
    box-shadow: 0 18px 40px -16px rgba(15,19,16,0.5);
    border: 1px solid rgba(244,247,238,0.08);
}
.haut-stick__inner { display: flex; align-items: center; justify-content: space-between; gap: 14px; max-width: 720px; margin: 0 auto; }
.haut-stick__txt { display: flex; flex-direction: column; line-height: 1.2; font-size: 13px; min-width: 0; }
.haut-stick__txt strong { font-size: 14px; font-weight: 600; color: var(--haut-cream); }
.haut-stick__txt span { font-size: 12px; color: rgba(244,247,238,0.6); }
.haut-btn--stick { background: var(--haut-accent); color: var(--haut-cta-ink); border-color: var(--haut-accent); flex: none; }
.haut-btn--stick:hover:not(:disabled) { background: var(--haut-cta-hover); color: var(--haut-white); }
@media (min-width: 720px) { .haut-stick { display: none; } }

@media (prefers-reduced-motion: reduce) {
    .haut *, .haut *::before, .haut *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}

/* ── Trust strip (images + cites + byline) — haut-* unique treatment ── */
.haut-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.haut-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .haut-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.haut-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid #84CC1633; box-shadow: 0 8px 32px -12px rgba(20,17,13,0.18);
}
.haut-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.haut-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.haut-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.haut-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.haut-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.haut-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #84CC1655;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.haut-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.haut-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #84CC16;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.haut-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.haut-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.haut-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.haut-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.haut-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.haut-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
